import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';

const DeletedRecords = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deletedRecords, setDeletedRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [recordType, setRecordType] = useState('all'); // 'all', 'phone_numbers', 'agents', 'users'

  // Check if user is admin
  const isAdmin = () => {
    const chatbotUser = localStorage.getItem('chatbotUser');
    let userEmail = '';
    
    if (chatbotUser) {
      try {
        const user = JSON.parse(chatbotUser);
        userEmail = user.email || '';
      } catch (e) {
        console.error('Error parsing chatbotUser:', e);
      }
    }
    
    if (!userEmail) {
      userEmail = localStorage.getItem('userEmail') || '';
    }
    
    return userEmail.toLowerCase() === 'info@urcommunity.com.au';
  };

  useEffect(() => {
    if (!isAdmin()) {
      setError('Access denied. Admin privileges required.');
      return;
    }
    fetchDeletedRecords();
  }, [recordType]);

  const fetchDeletedRecords = async () => {
    setLoading(true);
    setError(null);
    try {
      const chatbotUser = localStorage.getItem('chatbotUser');
      let userEmail = '';
      
      if (chatbotUser) {
        try {
          const user = JSON.parse(chatbotUser);
          userEmail = user.email || '';
        } catch (e) {
          console.error('Error parsing chatbotUser:', e);
        }
      }
      
      if (!userEmail) {
        userEmail = localStorage.getItem('userEmail') || '';
      }

      const url = recordType === 'all' 
        ? `${API_BASE_URL}/api/admin/deleted-records?email=${encodeURIComponent(userEmail)}`
        : `${API_BASE_URL}/api/admin/deleted-records?type=${recordType}&email=${encodeURIComponent(userEmail)}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-email': userEmail
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setDeletedRecords(data.records || []);
      } else {
        throw new Error(data.error || 'Failed to fetch deleted records');
      }
    } catch (err) {
      if (err.message.includes('Failed to fetch')) {
        setError('Backend server not running. Please start the server on port 3001.');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  };

  const getRecordTypeLabel = (type) => {
    const labels = {
      phone_number: 'Phone Number',
      agent: 'Agent',
      user: 'User',
      knowledge_base: 'Knowledge Base',
      other: 'Other'
    };
    return labels[type] || type;
  };

  const getRecordTypeIcon = (type) => {
    const icons = {
      phone_number: 'fas fa-phone',
      agent: 'fas fa-robot',
      user: 'fas fa-user',
      knowledge_base: 'fas fa-book',
      other: 'fas fa-trash'
    };
    return icons[type] || 'fas fa-trash';
  };

  const filteredRecords = deletedRecords.filter(record => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (record.deleted_phone_number || '').toLowerCase().includes(searchLower) ||
      (record.deleted_agent_id || '').toLowerCase().includes(searchLower) ||
      (record.deleted_user_email || '').toLowerCase().includes(searchLower) ||
      (record.user_email || '').toLowerCase().includes(searchLower) ||
      (record.record_type || '').toLowerCase().includes(searchLower) ||
      (record.reason || '').toLowerCase().includes(searchLower)
    );
  });

  if (!isAdmin()) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2 style={{ color: '#dc2626' }}>Access Denied</h2>
        <p>Admin privileges required to access this page.</p>
      </div>
    );
  }

  return (
    <div style={{
      background: '#f9fafb',
      minHeight: '100vh',
      padding: '24px'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h1 style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            color: '#111827',
            margin: '0 0 8px 0'
          }}>
            Deleted Records
          </h1>
          <p style={{
            fontSize: '0.875rem',
            color: '#6b7280',
            margin: 0
          }}>
            View all deleted records in the system
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            background: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '12px',
            color: '#dc2626',
            marginBottom: '16px'
          }}>
            {error}
            <button
              onClick={() => setError(null)}
              style={{
                background: 'none',
                border: 'none',
                color: '#dc2626',
                float: 'right',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              ×
            </button>
          </div>
        )}

        {/* Filters */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px',
            alignItems: 'end'
          }}>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                Filter by Type
              </label>
              <select
                value={recordType}
                onChange={(e) => setRecordType(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All Records</option>
                <option value="phone_numbers">Phone Numbers</option>
                <option value="agents">Agents</option>
                <option value="users">Users</option>
                <option value="knowledge_bases">Knowledge Bases</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                Search
              </label>
              <input
                type="text"
                placeholder="Search deleted records..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', color: '#7c3aed' }}></i>
          </div>
        )}

        {/* Records Table */}
        {!loading && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e5e7eb',
              background: '#f9fafb'
            }}>
              <h2 style={{
                fontSize: '1.125rem',
                fontWeight: '600',
                color: '#111827',
                margin: 0
              }}>
                Deleted Records ({filteredRecords.length})
              </h2>
            </div>
            <div style={{
              overflowX: 'auto',
              maxHeight: 'calc(100vh - 400px)',
              overflowY: 'auto'
            }}>
              {filteredRecords.length === 0 ? (
                <div style={{
                  padding: '40px',
                  textAlign: 'center',
                  color: '#6b7280'
                }}>
                  <i className="fas fa-trash" style={{ fontSize: '32px', marginBottom: '12px', opacity: 0.5 }}></i>
                  <p style={{ margin: 0 }}>No deleted records found</p>
                </div>
              ) : (
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse'
                }}>
                  <thead>
                    <tr style={{
                      background: '#f9fafb',
                      borderBottom: '1px solid #e5e7eb'
                    }}>
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#6b7280',
                        textTransform: 'uppercase'
                      }}>
                        Type
                      </th>
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#6b7280',
                        textTransform: 'uppercase'
                      }}>
                        Deleted Item
                      </th>
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#6b7280',
                        textTransform: 'uppercase'
                      }}>
                        User Email
                      </th>
                      {/* Price column - commented out as price field doesn't exist in deleted_phone_numbers table */}
                      {/* <th style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#6b7280',
                        textTransform: 'uppercase'
                      }}>
                        Price
                      </th> */}
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#6b7280',
                        textTransform: 'uppercase'
                      }}>
                        Deleted At
                      </th>
                      {/* Reason column - commented out as reason field doesn't exist in deleted_phone_numbers table */}
                      {/* <th style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#6b7280',
                        textTransform: 'uppercase'
                      }}>
                        Reason
                      </th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRecords.map((record, index) => (
                      <tr
                        key={index}
                        onClick={() => setSelectedRecord(selectedRecord === record ? null : record)}
                        style={{
                          cursor: 'pointer',
                          borderBottom: '1px solid #e5e7eb',
                          background: selectedRecord === record ? '#ede9fe' : 'white',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          if (selectedRecord !== record) {
                            e.currentTarget.style.background = '#f9fafb';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedRecord !== record) {
                            e.currentTarget.style.background = 'white';
                          }
                        }}
                      >
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <i className={getRecordTypeIcon(record.record_type)} style={{ fontSize: '14px', color: '#6b7280' }}></i>
                            <span style={{ fontSize: '14px', color: '#111827' }}>
                              {getRecordTypeLabel(record.record_type)}
                            </span>
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{ fontSize: '14px', color: '#111827', wordBreak: 'break-all' }}>
                            {record.deleted_phone_number || record.deleted_agent_id || record.deleted_user_email || 'N/A'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{ fontSize: '14px', color: '#111827' }}>
                            {record.user_email || 'N/A'}
                          </span>
                        </td>
                        {/* Price cell - commented out as price field doesn't exist in deleted_phone_numbers table */}
                        {/* <td style={{ padding: '12px 16px' }}>
                          <span style={{ fontSize: '14px', color: '#111827' }}>
                            {formatCurrency(record.deleted_phone_price || record.price)}
                          </span>
                        </td> */}
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{ fontSize: '14px', color: '#111827' }}>
                            {formatDate(record.deleted_at || record.created_at)}
                          </span>
                        </td>
                        {/* Reason cell - commented out as reason field doesn't exist in deleted_phone_numbers table */}
                        {/* <td style={{ padding: '12px 16px' }}>
                          <span style={{ fontSize: '14px', color: '#6b7280' }}>
                            {record.reason || 'N/A'}
                          </span>
                        </td> */}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeletedRecords;
